import React, { FunctionComponent, useContext, useRef, useState } from 'react';
import Button from '@atlaskit/button/new';
import MonacoEditor from 'react-monaco-editor';
import { editor, MarkerSeverity } from 'monaco-editor';
import SectionMessage from '@atlaskit/section-message';
import { TEST_ENDPOINT } from '~src/config';
import * as monacoEditor from 'monaco-editor/esm/vs/editor/editor.api';
import './expressions-playground-page-styles.css';
import { ThemeContext } from '~src/components/Settings/theme-context';

const examplePayload = `{
  "eventType": "avi:jira:created:issue",
  "issue": {
    "id": "10073",
    "key": "SP-10",
    "fields": {
      "summary": "A descriptive title",
      "issuetype": {
        "self": "https://example.atlassian.net/rest/api/2/issuetype/10001",
        "id": "10001",
        "description": "Functionality or a feature expressed as a user goal.",
        "iconUrl": "https://example.atlassian.net/secure/viewavatar?size=medium&avatarId=10315&avatarType=issuetype",
        "name": "Story",
        "subtask": false,
        "avatarId": 10315
      },
      "creator": {
        "accountId": "4ad9aa0c52dc1b420a791d12"
      },
      "created": "2021-01-20T15:58:12.797+1100",
      "project": {
        "self": "https://example.atlassian.net/rest/api/2/project/10000",
        "id": "10000",
        "key": "SP",
        "name": "Sample Project",
        "projectTypeKey": "software",
        "simplified": false,
        "avatarUrls": {
          "48x48": "https://example.atlassian.net/secure/projectavatar?pid=10000&avatarId=10419",
          "24x24": "https://example.atlassian.net/secure/projectavatar?size=small&s=small&pid=10000&avatarId=10419",
          "16x16": "https://example.atlassian.net/secure/projectavatar?size=xsmall&s=xsmall&pid=10000&avatarId=10419",
          "32x32": "https://example.atlassian.net/secure/projectavatar?size=medium&s=medium&pid=10000&avatarId=10419"
        }
      },
      "reporter": {
        "accountId": "4ad9aa0c52dc1b420a791d12"
      },
      "assignee": null,
      "updated": "2021-01-20T15:58:12.797+1100",
      "status": {
        "self": "https://example.atlassian.net/rest/api/2/status/10000",
        "description": "",
        "iconUrl": "https://example.atlassian.net/",
        "name": "Backlog",
        "id": "10000",
        "statusCategory": {
          "self": "https://example.atlassian.net/rest/api/2/statuscategory/2",
          "id": 2,
          "key": "new",
          "colorName": "blue-gray",
          "name": "To Do"
        }
      }
    }
  },
  "atlassianId": "4ad9aa0c52dc1b420a791d12",
  "associatedUsers": [
    {
      "accountId": "4ad9aa0c52dc1b420a791d12"
    }
  ]
}`;

const exampleExpression = `event.issue.fields.project.name.includes('Sample')`;

interface TestExpressionInputData {
  payload: string;
  expression: string;
}

interface FlattenErrorMessage {
  type: string;
  location: string;
  message: string;
}

interface TestExpressionResponse {
  executionResult?: unknown;
  evaluationDuration?: number;
  errors?: FlattenErrorMessage[];
  isError?: boolean;
  isInterpreterError?: boolean;
  traceId?: string | null;
  validFilterExpression?: boolean;
}

interface TestExpressionState {
  executionResult?: unknown;
  duration?: number;
  errors?: FlattenErrorMessage[];
  isError?: boolean;
  isInterpreterError?: boolean;
  traceId?: string | null;
  validFilterExpression?: boolean;
}

const testExpression = async (
  data: TestExpressionInputData,
): Promise<TestExpressionResponse> => {
  try {
    const response = await fetch(`${TEST_ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorBody = (await response.json()) as {
        executionResult: unknown;
        validFilterExpression: boolean;
        expressionComplexity: {
          formula: string;
        };
        errors: Record<
          string,
          Array<{
            reason: string;
            errorMessages: Array<string>;
          }>
        >;
        duration: number;
      };
      console.log(errorBody);
      const errorsCollection = Object.entries(errorBody.errors).flatMap(
        ([type, groups]) =>
          groups.flatMap((errorGroup) =>
            errorGroup.errorMessages.map((message) => {
              return {
                type: type,
                location: errorGroup.reason,
                message:
                  message +
                  (type === 'COMPLEXITY' && errorBody.expressionComplexity
                    ? ` Your complexity: ${errorBody.expressionComplexity.formula}`
                    : ''),
              } as FlattenErrorMessage;
            }),
          ),
      );
      console.error('Error:', errorsCollection);
      return {
        isError: true,
        errors: errorsCollection,
        isInterpreterError: (errorBody.errors.INTERPRETER || []).length > 0,
        traceId: response.headers.get('atl-traceid'),
        validFilterExpression: errorBody.validFilterExpression,
        executionResult: errorBody.executionResult,
      };
    }
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    return {
      isError: true,
      errors: [
        {
          type: 'FAILURE',
          location: 'Request',
          message: 'Unknown error, look at the console logs',
        },
      ],
    };
  }
};

const ExpressionsPlaygroundPage: FunctionComponent = () => {
  const [result, setResult] = useState<TestExpressionState>({});
  const payloadEditorRef = useRef<editor.IStandaloneCodeEditor>();
  const expressionEditorRef = useRef<editor.IStandaloneCodeEditor>();
  const payloadMonacoRef = useRef<typeof monacoEditor>();
  const theme =
    useContext(ThemeContext).theme?.colorMode === 'light'
      ? 'vs-light'
      : 'vs-dark';

  const validateInputData = (
    data: TestExpressionInputData,
  ): FlattenErrorMessage[] => {
    const errorsCollection: FlattenErrorMessage[] = [];

    if (!data.payload) {
      errorsCollection.push({
        type: 'PRE-CHECK',
        location: 'payload',
        message: "Event payload can't be empty",
      });
    }
    if (!data.expression) {
      errorsCollection.push({
        type: 'PRE-CHECK',
        location: 'expression',
        message: "Expression can't be empty",
      });
    }

    const monacoErrorsCollection = new Map<
      string,
      { location: string[]; message: string }
    >();
    payloadMonacoRef.current?.editor
      .getModelMarkers({ owner: 'json' })
      .forEach((marker) => {
        if (marker.severity === MarkerSeverity.Error) {
          const code = `${marker.code}`;
          const message = marker.message;
          const startLineNumber = marker.startLineNumber;
          const endColumn = marker.endColumn;

          const existingErrorCollection = monacoErrorsCollection.get(code);
          if (existingErrorCollection) {
            existingErrorCollection.location.push(
              `(line ${startLineNumber}, column ${endColumn})`,
            );
          } else {
            monacoErrorsCollection.set(code, {
              location: [`(line ${startLineNumber}, column ${endColumn})`],
              message: message,
            });
          }
        }
      });

    monacoErrorsCollection.forEach((errorCollection) => {
      const location = `${errorCollection.location.join(', ')}`;
      errorsCollection.push({
        type: 'PAYLOAD-CHECK',
        location: `Payload: ${location}`,
        message: errorCollection.message,
      });
    });
    return errorsCollection;
  };

  const handleSubmit = async () => {
    const data: TestExpressionInputData = {
      payload: payloadEditorRef.current?.getValue() || '',
      expression: expressionEditorRef.current?.getValue() || '',
    };
    const errorsCollection: FlattenErrorMessage[] = validateInputData(data);

    if (errorsCollection.length) {
      setResult({ isError: true, errors: errorsCollection });
      return;
    }

    const result = await testExpression(data);
    setResult(result);
  };

  const getTestResult = () => {
    if (result.executionResult === true) {
      return `${result.executionResult} (event will not be filtered out)`;
    }

    if (result.executionResult === false) {
      return `${result.executionResult} (event will be filtered out)`;
    }

    return `${JSON.stringify(
      result.executionResult,
    )} (not a boolean, event will be filtered out based on the value of the "onError" property)`;
  };

  return (
    <div className={'expressions-playground-main-container'}>
      <div
        role={'region'}
        className={'expressions-playground-test-result-section'}
        aria-live={'polite'}
        aria-atomic={true}
      >
        <SectionMessage
          title={`Test result: ${
            result.validFilterExpression
              ? getTestResult()
              : result.isError
              ? 'error'
              : ''
          }`}
          appearance={!result.isError ? 'information' : 'error'}
        >
          {!result.isError ? (
            ''
          ) : (
            <table className={'expressions-playground-error-table'}>
              <thead aria-hidden={true}>
                <tr>
                  <th
                    className={'expressions-playground-error-table-type-cell'}
                  >
                    {'Error type'}
                  </th>
                  <th
                    className={
                      'expressions-playground-error-table-location-cell'
                    }
                  >
                    {'Location'}
                  </th>
                  <th
                    className={
                      'expressions-playground-error-table-message-cell'
                    }
                  >
                    {'Message'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.errors?.map((er, index) => (
                  <tr key={`test-expression-error-${index}`}>
                    <td aria-label={'Error type:'}>{er.type}</td>
                    <td aria-label={'Location:'}>{er.location}</td>
                    <td aria-label={'Message:'}>{er.message}</td>
                  </tr>
                ))}
                {result.isInterpreterError && (
                  <tr key={`test-expression-error-interpreter`}>
                    <td colSpan={3}>
                      {'Server side error occurred. We are on investigating what went wrong. If you need help contact Atlassian. Problem identification: ' +
                        result.traceId}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          {result.duration ? (
            <span>
              {`Evaluation duration: ${result.duration / 1000000.0} ms`}
            </span>
          ) : (
            ''
          )}
        </SectionMessage>
      </div>
      <div className={'expressions-playground-code-editors-main-container'}>
        <div className={'expressions-playground-code-editor-container'}>
          <h3>{'Payload'}</h3>
          <div
            className={
              'expressions-playground-code-editor-container-level-deeper'
            }
            aria-label={'Payload editor'}
          >
            <MonacoEditor
              language={'json'}
              value={examplePayload}
              options={{
                cursorStyle: 'line',
                formatOnPaste: true,
                formatOnType: true,
                wordWrap: 'on',
                minimap: {
                  enabled: false,
                },
                automaticLayout: true,
                theme: theme,
              }}
              editorDidMount={(editor, monaco) => {
                payloadEditorRef.current = editor;
                payloadMonacoRef.current = monaco;
                setTimeout(function () {
                  editor.getAction('editor.action.formatDocument')?.run();
                  monaco.editor.setTheme(theme);
                });
              }}
            />
          </div>
        </div>
        <div className={'expressions-playground-code-editor-container'}>
          <h3>{'Expression'}</h3>
          <div
            className={
              'expressions-playground-code-editor-container-level-deeper'
            }
            aria-label={'Expression editor'}
          >
            <MonacoEditor
              language={'jira-expressions-language'}
              value={exampleExpression}
              options={{
                cursorStyle: 'line',
                formatOnPaste: true,
                formatOnType: true,
                wordWrap: 'on',
                minimap: {
                  enabled: false,
                },
                automaticLayout: true,
                theme: theme,
              }}
              editorDidMount={(editor, monaco) => {
                expressionEditorRef.current = editor;
                setTimeout(function () {
                  editor.getAction('editor.action.formatDocument')?.run();
                  monaco.editor.setTheme(theme);
                });
              }}
            />
          </div>
        </div>
      </div>
      <div className={'expressions-playground-main-container-footer'}>
        <Button appearance="primary" type="submit" onClick={handleSubmit}>
          {'Test'}
        </Button>
      </div>
    </div>
  );
};

export default ExpressionsPlaygroundPage;
